import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Milliseconds the card must be visible in the viewport without scrolling
 * before the dynamic animation phase triggers.
 */
const DYNAMIC_IDLE_MS = 1000;

/**
 * Milliseconds of scroll inactivity before we consider scrolling "stopped"
 * for the purpose of starting the dynamic idle timer.
 */
const SCROLL_SETTLE_MS = 150;

interface UseCardAnimationOptions {
  /**
   * IntersectionObserver threshold (0-1) for entrance visibility.
   * The card fades in when this fraction is visible.
   * Default 0.7 (70% of card visible = fade in).
   */
  threshold?: number;
  /** When true, no observer is created and animation states stay false */
  disabled?: boolean;
  /** Delay in ms before dynamic phase begins after entrance starts. Default 0 (no delay) */
  entranceDuration?: number;
}

interface UseCardAnimationResult {
  /** Ref to attach to the card container element */
  containerRef: React.RefObject<HTMLDivElement>;
  /**
   * True when card is sufficiently visible in the viewport (>=30%).
   * Stays true as long as the card remains visible — NOT interrupted by scrolling.
   * Only resets when the card leaves the visibility threshold.
   */
  isEntranceActive: boolean;
  /**
   * True after the card has been visible for >1s without scrolling.
   * Interrupted by scrolling (returns to false / static state).
   * Re-triggers after scrolling stops and 1s idle elapses again.
   */
  isDynamicActive: boolean;
  /** @deprecated Use isEntranceActive instead. Alias for isEntranceActive. */
  isAnimating: boolean;
  /** True after onImageLoad has been called */
  isImageLoaded: boolean;
  /** Call this from the image's onLoad handler */
  onImageLoad: () => void;
}

/**
 * Shared hook for card scroll-triggered animations.
 *
 * Two independent phases:
 *
 * 1. **Entrance** (`isEntranceActive`): Driven purely by IntersectionObserver.
 *    Becomes true when the threshold fraction of the card is visible, stays true
 *    until the card leaves that threshold. Scrolling does NOT reset entrance.
 *
 * 2. **Dynamic** (`isDynamicActive`): Triggered after the card has been visible
 *    for >1s without any scrolling. Interrupted immediately when scrolling occurs
 *    (returns to static/initial state). Re-triggers after scroll settles + 1s idle.
 *
 * Respects `prefers-reduced-motion: reduce` — behaves as disabled.
 */
export function useCardAnimation(options: UseCardAnimationOptions = {}): UseCardAnimationResult {
  const { threshold = 0.7, disabled = false, entranceDuration = 0 } = options;

  const containerRef = useRef<HTMLDivElement>(null);
  const [isEntranceActive, setIsEntranceActive] = useState(false);
  const [isDynamicActive, setIsDynamicActive] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  // Check prefers-reduced-motion
  const prefersReducedMotion =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const isDisabled = disabled || prefersReducedMotion;

  // One-way flag for image load
  const onImageLoad = useCallback(() => {
    setIsImageLoaded(true);
  }, []);

  // Timers for dynamic phase
  const dynamicTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const scrollSettleTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (isDisabled) return undefined;

    const element = containerRef.current;
    if (!element) return undefined;

    let isVisible = false;

    /**
     * Clear and cancel dynamic phase timers.
     */
    const cancelDynamic = () => {
      setIsDynamicActive(false);
      if (dynamicTimerRef.current) {
        clearTimeout(dynamicTimerRef.current);
        dynamicTimerRef.current = undefined;
      }
      if (scrollSettleTimerRef.current) {
        clearTimeout(scrollSettleTimerRef.current);
        scrollSettleTimerRef.current = undefined;
      }
    };

    /**
     * Start the dynamic idle timer chain:
     * Wait for scroll to settle (SCROLL_SETTLE_MS), then wait for
     * entranceDuration + DYNAMIC_IDLE_MS before activating dynamic state.
     */
    const startDynamicTimer = () => {
      if (!isVisible) return;

      // Clear any pending timers
      if (dynamicTimerRef.current) clearTimeout(dynamicTimerRef.current);
      if (scrollSettleTimerRef.current) clearTimeout(scrollSettleTimerRef.current);

      // After scroll settles, start the dynamic delay
      scrollSettleTimerRef.current = setTimeout(() => {
        const totalDelay = entranceDuration + DYNAMIC_IDLE_MS;
        dynamicTimerRef.current = setTimeout(() => {
          if (isVisible) {
            setIsDynamicActive(true);
          }
        }, totalDelay);
      }, SCROLL_SETTLE_MS);
    };

    // Scroll handler: interrupt dynamic state, restart timer
    const handleScroll = () => {
      // Scrolling interrupts dynamic phase — return to static
      cancelDynamic();

      // Restart the timer chain (will fire after scroll settles + idle period)
      if (isVisible) {
        startDynamicTimer();
      }
    };

    // IntersectionObserver: drives entrance state
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          isVisible = entry.isIntersecting;

          if (isVisible) {
            // Card entered viewport — activate entrance
            setIsEntranceActive(true);
            // Start the dynamic timer chain
            startDynamicTimer();
          } else {
            // Card left viewport — deactivate everything
            setIsEntranceActive(false);
            cancelDynamic();
          }
        });
      },
      { threshold }
    );

    observer.observe(element);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
      cancelDynamic();
    };
  }, [isDisabled, threshold, entranceDuration]);

  // When disabled, always return false
  if (isDisabled) {
    return {
      containerRef,
      isEntranceActive: false,
      isDynamicActive: false,
      isAnimating: false,
      isImageLoaded,
      onImageLoad,
    };
  }

  return {
    containerRef,
    isEntranceActive,
    isDynamicActive,
    isAnimating: isEntranceActive,
    isImageLoaded,
    onImageLoad,
  };
}
